import { useMemo, useRef } from 'react';

import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import dayjs, { Dayjs } from 'dayjs';

import { useGetStudentCourseEnrollments } from 'api/hooks/private/users/students/GetStudentCourseEnrollments';
import { useGetStudentPeriodEnrollments } from 'api/hooks/private/users/students/GetStudentPeriods';
import { EligibilityBlock } from 'api/hooks/private/users/students/useGetSelfScheduleEligibility';

import useAuthContext from 'hooks/useAuthContext';
import useSelfScheduleContext from 'hooks/useSelfScheduleContext';

import { isDayBlocked } from 'utils/eligibilityHelpers';

import EligibilityBlockAlert from '../EligibilityBlockAlert';
import CalendarDay from './components/CalendarDay';
import CalendarLegend from './components/CalendarLegend';

interface SelfScheduleMenuProps {
  eligibilityBlocks: EligibilityBlock[];
}

function SelfScheduleMenu({ eligibilityBlocks }: SelfScheduleMenuProps): JSX.Element {
  const theme = useTheme();

  // get the student id from the auth context
  const { userId: studentId } = useAuthContext();

  const calendarRef = useRef<HTMLDivElement>(null);
  const { periodCountLoading, periodCount, selfScheduleParams, setSelfScheduleParams } = useSelfScheduleContext();

  // fetch student course enrollments
  const courseEnrollments = useGetStudentCourseEnrollments({
    variables: {
      id: studentId ?? '',
    },
    enabled: !!studentId,
  });

  // fetch student period enrollments
  const periodEnrollments = useGetStudentPeriodEnrollments({
    variables: {
      id: studentId ?? '',
    },
    enabled: !!studentId,
  });

  // get a lisft of self scheduled course enrollments
  const selfScheduledEnrollments = useMemo(
    () => courseEnrollments.data?.filter((enrollment) => enrollment?.course?.schedule_type === 'self_scheduled') ?? [],
    [courseEnrollments],
  );

  // filter permanent eligibility blocks
  const permanentBlocks = useMemo(
    () => eligibilityBlocks.filter((block) => block.type === 'blocked_indefinitely'),
    [eligibilityBlocks],
  );

  // filter non-permanent eligibility blocks
  const nonPermanentBlocks = useMemo(
    () => eligibilityBlocks.filter((block) => block.type !== 'blocked_indefinitely') ?? [],
    [eligibilityBlocks],
  );

  const blockedDates = useMemo(() => {
    const blocks = nonPermanentBlocks ?? [];
    const dates = new Set<string>();

    // For each day in the selected month, check if any block affects it
    const monthStart = selfScheduleParams.selectedMonth.startOf('month');
    const monthEnd = selfScheduleParams.selectedMonth.endOf('month');

    let currentDay = monthStart;
    while (currentDay.isBefore(monthEnd) || currentDay.isSame(monthEnd, 'day')) {
      if (isDayBlocked(currentDay, blocks)) {
        dates.add(currentDay.format('YYYY-MM-DD'));
      }
      currentDay = currentDay.add(1, 'day');
    }

    return dates;
  }, [nonPermanentBlocks, selfScheduleParams]);

  return (
    <Stack
      spacing={2}
      px={2}
      maxWidth={{
        md: 'min-content',
      }}
    >
      <DateCalendar
        ref={calendarRef}
        disablePast
        renderLoading={() => <DayCalendarSkeleton />}
        loading={periodCountLoading}
        onChange={(value: Dayjs) => {
          // props.onDateChange(value);
          setSelfScheduleParams((prev) => ({
            ...prev,
            selectedDate: value,
          }));
        }}
        onMonthChange={(value: Dayjs) => {
          // setSelectedMonth(value);
          setSelfScheduleParams((prev) => ({
            ...prev,
            selectedMonth: value,
          }));
        }}
        slots={{
          day: CalendarDay,
        }}
        slotProps={{
          day: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            coursePeriodCount: periodCount,
            studentBookedDates: periodEnrollments?.data
              ?.filter((enrollment) => !enrollment?.canceled_at)
              .map((enrollment) => dayjs(enrollment.course_period.start).format('YYYY-MM-DD')),
            blockedDates: blockedDates,
          },
        }}
        sx={{
          alignSelf: 'center',
        }}
      />

      <CalendarLegend
        legendItems={[
          {
            color: theme.palette.grey[400],
            label: 'Not eligible to book',
          },
          {
            color: theme.palette.error.main,
            label: 'No Drives Available',
          },
          {
            color: theme.palette.warning.main,
            label: 'Less Than Two Drives Available',
          },
          {
            color: theme.palette.success.main,
            label: 'Drives Available',
          },
        ]}
      />

      {/* Eligibility alerts for blocked_indefinitely blocks */}
      {permanentBlocks.length > 0 && (
        <Stack spacing={2}>
          {permanentBlocks.map((block) => (
            <EligibilityBlockAlert key={block.id} block={block} />
          ))}
        </Stack>
      )}

      <Stack spacing={1} width="100%">
        <Typography
          component={InputLabel}
          htmlFor="course_select"
          variant="subtitle1"
          color="text.primary"
          fontWeight={700}
        >
          Course
        </Typography>
        <TextField
          id="course_select"
          select
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
          helperText="Select the course you want to schedule"
          value={selfScheduleParams?.selectedEnrollment?.id ?? ''}
          onChange={(event) => {
            // setSelectedEnrollment(event.target.value);
            // props.onEnrollmentChange(event.target.value);
            setSelfScheduleParams((prev) => ({
              ...prev,
              selectedEnrollment: courseEnrollments?.data?.find((enrollment) => enrollment?.id === event.target.value),
            }));
          }}
        >
          {selfScheduledEnrollments?.map((enrollment) => {
            return (
              <MenuItem value={enrollment?.id} key={enrollment?.id} disabled={!!enrollment?.completed_at}>
                {enrollment?.course_section?.title + (enrollment?.completed_at ? ' (Completed)' : '')}
              </MenuItem>
            );
          })}
        </TextField>
      </Stack>
    </Stack>
  );
}

export default SelfScheduleMenu;
